import { collabViewToDatabaseView } from '@databases/collab/convert/to-database';
import { type YDatabase } from '@databases/collab/y-database';
import { type ViewSdkContract } from '@databases/sdk-types';
import {
  type DatabaseFilter,
  type DatabaseLayoutType
} from '@databases/types/database/view';
import { type TypedViewLayoutRegistry } from '@databases/view-layout-modules';
import { err, ok, type Result } from 'neverthrow';

import { assertFieldExists } from '../domain/guards/assert-field-exists.ts';
import { assertIndexInRange } from '../domain/guards/assert-index-in-range.ts';
import { assertViewExists } from '../domain/guards/assert-view-exists.ts';
import { assertViewNotExists } from '../domain/guards/assert-view-not-exists.ts';
import { parse, tryMutate } from '../domain/result.ts';
import {
  addSortSchema,
  applyViewNameDeltaSchema,
  clearFilterSchema,
  hideFieldsSchema,
  removeViewSchema,
  removeSortSchema,
  reorderViewSchema,
  setCardWidthSchema,
  setDefaultViewSchema,
  setFilterSchema,
  setGroupFieldSchema,
  setLayoutTypeSchema,
  setPropertiesSchema,
  setNumberedEntriesSchema,
  setShowFieldNamesSchema,
  showFieldsSchema,
  updateLayoutSchema,
  updatePropertiesSchema,
  updateNameSchema,
  updateSortSchema,
  type AddSortInput,
  type ApplyViewNameDeltaInput,
  type ClearFilterInput,
  type HideFieldsInput,
  type RemoveViewInput,
  type RemoveSortInput,
  type ReorderViewInput,
  type SetCardWidthInput,
  type SetDefaultViewInput,
  type SetFilterInput,
  type SetGroupFieldInput,
  type SetLayoutTypeInput,
  type SetPropertiesInput,
  type SetNumberedEntriesInput,
  type SetShowFieldNamesInput,
  type ShowFieldsInput,
  type UpdateLayoutInput,
  type UpdatePropertiesInput,
  type UpdateNameInput,
  type UpdateSortInput
} from '../domain/views/schemas.ts';
import { addSavedView } from '../handlers/view/add-saved-view.ts';
import { bulkApplySavedViewChanges } from '../handlers/view/bulk-apply-saved-view-changes.ts';
import { deleteSavedView } from '../handlers/view/delete-saved-view.ts';
import { reorderSavedView } from '../handlers/view/reorder-saved-view.ts';
import { setDefaultSavedView } from '../handlers/view/set-default-saved-view.ts';
import { setSavedView } from '../handlers/view/set-saved-view.ts';
import { updateSavedViewName } from '../handlers/view/update-saved-view-name.ts';
import { ViewChangeType } from '../handlers/view/view-change-types.ts';
import { type DatabaseView, type SdkError } from '../types.ts';

import {
  builtInOnlyWritePolicy,
  type ViewLayoutWritePolicy,
  type ViewLayoutWritePolicyError
} from './views/view-layout-write-policy.ts';

export type ViewSdkOptions<
  PropertiesMap extends Record<string, Record<string, unknown>> = Record<never, never>
> = {
  /**
   * Controls which layout IDs are writable via `views.add()` and
   * `views.update()`. Defaults to {@link builtInOnlyWritePolicy}, which
   * permits only built-in layouts (table, gallery, board, calendar).
   *
   * When a `viewLayoutRegistry` is provided, the write policy is derived
   * from it automatically. This field is ignored when `viewLayoutRegistry`
   * is set.
   */
  viewLayoutWritePolicy?: ViewLayoutWritePolicy;

  /**
   * Typed registry that provides converter-aware read/write methods and
   * acts as the write policy. When provided, the SDK gains typed overloads
   * for property methods.
   */
  viewLayoutRegistry?: TypedViewLayoutRegistry<PropertiesMap>;
};

/**
 * Public API for reading and mutating database views.
 *
 * Every mutation runs a three-step pipeline:
 *   1. **Zod validation** — rejects malformed input → `VALIDATION_ERROR`
 *   2. **Business rules** — rejects invalid document state → `STATE_ERROR`
 *      (VIEW_NOT_FOUND, VIEW_ALREADY_EXISTS, FIELD_NOT_FOUND, INDEX_OUT_OF_RANGE)
 *   3. **Handler mutation** — delegates to collab; only truly unexpected
 *      throws surface as `INTERNAL_ERROR`
 *
 * Views are **not** gated by `isLocked` — view configuration stays allowed
 * when the database structure is locked.
 *
 * @typeParam PropertiesMap - When populated via `viewLayoutRegistry`,
 *   property methods gain typed overloads keyed by layout ID.
 */
export class ViewSdk<
  PropertiesMap extends Record<string, Record<string, unknown>> = Record<never, never>
> implements ViewSdkContract<PropertiesMap> {
  private readonly viewLayoutWritePolicy: ViewLayoutWritePolicy;
  private readonly viewLayoutRegistry?: TypedViewLayoutRegistry<PropertiesMap>;

  constructor(
    private readonly ydb: YDatabase,
    options: ViewSdkOptions<PropertiesMap> = {}
  ) {
    this.viewLayoutRegistry = options.viewLayoutRegistry;
    this.viewLayoutWritePolicy =
      options.viewLayoutRegistry ??
      options.viewLayoutWritePolicy ??
      builtInOnlyWritePolicy;
  }

  private mapPolicyError(error: ViewLayoutWritePolicyError): SdkError {
    return {
      type: 'STATE_ERROR',
      reason: error.reason,
      message: error.message
    };
  }

  private requireWritableLayout(layoutType: string): Result<void, SdkError> {
    return this.viewLayoutWritePolicy
      .requireWritable(layoutType)
      .mapErr((error) => this.mapPolicyError(error));
  }

  add(view: DatabaseView): Result<void, SdkError> {
    return this.requireWritableLayout(view.settings.layout.type)
      .andThen(() => assertViewNotExists(this.ydb, view.id))
      .andThen(() => tryMutate(() => addSavedView(this.ydb, view)));
  }

  remove(input: RemoveViewInput): Result<void, SdkError> {
    return parse(removeViewSchema, input).andThen(({ viewId }) =>
      assertViewExists(this.ydb, viewId).andThen(() =>
        tryMutate(() => deleteSavedView(this.ydb, viewId))
      )
    );
  }

  setDefault(input: SetDefaultViewInput): Result<void, SdkError> {
    return parse(setDefaultViewSchema, input).andThen(({ viewId }) =>
      assertViewExists(this.ydb, viewId).andThen(() =>
        tryMutate(() => setDefaultSavedView(this.ydb, viewId))
      )
    );
  }

  update(view: DatabaseView): Result<void, SdkError> {
    return this.requireWritableLayout(view.settings.layout.type)
      .andThen(() => assertViewExists(this.ydb, view.id))
      .andThen(() => tryMutate(() => setSavedView(this.ydb, view)));
  }

  setLayoutType(input: SetLayoutTypeInput): Result<void, SdkError> {
    return parse(setLayoutTypeSchema, input).andThen(({ viewId, layoutType }) =>
      this.requireWritableLayout(layoutType)
        .andThen(() => assertViewExists(this.ydb, viewId))
        .andThen(() =>
          tryMutate(() =>
            bulkApplySavedViewChanges(this.ydb, [
              {
                type: ViewChangeType.UPDATE_LAYOUT_TYPE,
                viewId,
                newLayoutType: layoutType
              }
            ])
          )
        )
    );
  }

  setCardWidth(input: SetCardWidthInput): Result<void, SdkError> {
    return parse(setCardWidthSchema, input).andThen(({ viewId, cardWidth }) =>
      assertViewExists(this.ydb, viewId).andThen(() =>
        tryMutate(() =>
          bulkApplySavedViewChanges(this.ydb, [
            {
              type: ViewChangeType.UPDATE_LAYOUT_CARD_WIDTH,
              viewId,
              newCardWidth: cardWidth
            }
          ])
        )
      )
    );
  }

  setNumberedEntries(input: SetNumberedEntriesInput): Result<void, SdkError> {
    return parse(setNumberedEntriesSchema, input).andThen(({ viewId, enabled }) =>
      assertViewExists(this.ydb, viewId).andThen(() =>
        tryMutate(() =>
          bulkApplySavedViewChanges(this.ydb, [
            {
              type: ViewChangeType.UPDATE_LAYOUT_SHOW_NUMBERED_ENTRIES,
              viewId,
              newShowNumberedEntries: enabled
            }
          ])
        )
      )
    );
  }

  setShowFieldNames(input: SetShowFieldNamesInput): Result<void, SdkError> {
    return parse(setShowFieldNamesSchema, input).andThen(({ viewId, enabled }) =>
      assertViewExists(this.ydb, viewId).andThen(() =>
        tryMutate(() =>
          bulkApplySavedViewChanges(this.ydb, [
            {
              type: ViewChangeType.UPDATE_LAYOUT_SHOW_FIELD_NAMES,
              viewId,
              newShowFieldNames: enabled
            }
          ])
        )
      )
    );
  }

  setGroupField(input: SetGroupFieldInput): Result<void, SdkError> {
    return parse(setGroupFieldSchema, input).andThen(({ viewId, fieldId }) =>
      assertViewExists(this.ydb, viewId)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() =>
          tryMutate(() =>
            bulkApplySavedViewChanges(this.ydb, [
              {
                type: ViewChangeType.SET_GROUPING_FIELD,
                viewId,
                fieldId,
                manualGroupOrder: []
              }
            ])
          )
        )
    );
  }

  updateLayout(input: UpdateLayoutInput): Result<void, SdkError> {
    return parse(updateLayoutSchema, input).andThen(
      ({
        viewId,
        layoutType,
        groupFieldId,
        numberedEntries,
        cardWidth,
        showFieldNames
      }) =>
        (layoutType ? this.requireWritableLayout(layoutType) : ok(undefined))
          .andThen(() => assertViewExists(this.ydb, viewId))
          .andThen(() =>
            groupFieldId ? assertFieldExists(this.ydb, groupFieldId) : ok(undefined)
          )
          .andThen(() => {
            const changes: Parameters<typeof bulkApplySavedViewChanges>[1] = [];

            if (layoutType) {
              changes.push({
                type: ViewChangeType.UPDATE_LAYOUT_TYPE,
                viewId,
                newLayoutType: layoutType
              });
            }

            if (groupFieldId) {
              changes.push({
                type: ViewChangeType.SET_GROUPING_FIELD,
                viewId,
                fieldId: groupFieldId,
                manualGroupOrder: []
              });
            }

            if (numberedEntries !== undefined) {
              changes.push({
                type: ViewChangeType.UPDATE_LAYOUT_SHOW_NUMBERED_ENTRIES,
                viewId,
                newShowNumberedEntries: numberedEntries
              });
            }

            if (cardWidth) {
              changes.push({
                type: ViewChangeType.UPDATE_LAYOUT_CARD_WIDTH,
                viewId,
                newCardWidth: cardWidth
              });
            }

            if (showFieldNames !== undefined) {
              changes.push({
                type: ViewChangeType.UPDATE_LAYOUT_SHOW_FIELD_NAMES,
                viewId,
                newShowFieldNames: showFieldNames
              });
            }

            if (changes.length === 0) {
              return ok(undefined);
            }

            return tryMutate(() => bulkApplySavedViewChanges(this.ydb, changes));
          })
    );
  }

  private layoutSlot(
    viewId: string,
    layoutType: string
  ): Record<string, unknown> | undefined {
    const bag = this.ydb.viewsById[viewId]?.properties;
    return bag?.[layoutType] as Record<string, unknown> | undefined;
  }

  setProperties<K extends keyof PropertiesMap & string>(input: {
    viewId: string;
    layoutType: K;
    properties: PropertiesMap[K];
  }): Result<void, SdkError>;
  setProperties(input: {
    viewId: string;
    properties: Record<string, unknown> | undefined;
  }): Result<void, SdkError>;
  setProperties(
    input: SetPropertiesInput & { layoutType?: string }
  ): Result<void, SdkError> {
    const { layoutType, ...rest } = input;

    return parse(setPropertiesSchema, rest).andThen(({ viewId, properties }) =>
      assertViewExists(this.ydb, viewId)
        .andThen((view) => this.requireWritableLayout(layoutType ?? view.layoutType))
        .andThen(() => {
          // Untyped whole-bag replace (escape hatch): no layout namespacing.
          if (!layoutType) {
            return tryMutate(() =>
              bulkApplySavedViewChanges(this.ydb, [
                { type: ViewChangeType.SET_PROPERTIES, viewId, properties }
              ])
            );
          }

          // Typed write: `properties` is stored under the layout's own slot in
          // the namespaced bag, so writing one layout never touches another's
          // settings. `undefined` clears just this layout's slot.
          if (properties === undefined) {
            return tryMutate(() =>
              bulkApplySavedViewChanges(this.ydb, [
                { type: ViewChangeType.UPDATE_PROPERTIES, viewId, unset: [layoutType] }
              ])
            );
          }

          const module = this.viewLayoutRegistry?.getModule(layoutType);
          const slot = module?.converter
            ? module.converter.toDatabase(properties)
            : properties;

          return tryMutate(() =>
            bulkApplySavedViewChanges(this.ydb, [
              {
                type: ViewChangeType.UPDATE_PROPERTIES,
                viewId,
                set: { [layoutType]: slot }
              }
            ])
          );
        })
    );
  }

  updateProperties<K extends keyof PropertiesMap & string>(input: {
    viewId: string;
    layoutType: K;
    set?: Partial<PropertiesMap[K]>;
    unset?: (keyof PropertiesMap[K] & string)[];
  }): Result<void, SdkError>;
  updateProperties(input: {
    viewId: string;
    set?: Record<string, unknown>;
    unset?: string[];
  }): Result<void, SdkError>;
  updateProperties(
    input: UpdatePropertiesInput & { layoutType?: string }
  ): Result<void, SdkError> {
    const { layoutType, ...rest } = input;

    return parse(updatePropertiesSchema, rest).andThen(({ viewId, set, unset }) =>
      assertViewExists(this.ydb, viewId)
        .andThen((view) => this.requireWritableLayout(layoutType ?? view.layoutType))
        .andThen(() => {
          // Untyped top-level patch (escape hatch).
          if (!layoutType) {
            return tryMutate(() =>
              bulkApplySavedViewChanges(this.ydb, [
                { type: ViewChangeType.UPDATE_PROPERTIES, viewId, set, unset }
              ])
            );
          }

          // Typed patch: merge into this layout's slot (read-modify-write) so the
          // whole slot is rewritten while sibling layouts stay untouched.
          const merged: Record<string, unknown> = {
            ...(this.layoutSlot(viewId, layoutType) ?? {}),
            ...(set ?? {})
          };
          for (const key of unset ?? []) {
            delete merged[key];
          }

          const module = this.viewLayoutRegistry?.getModule(layoutType);
          const slot = module?.converter ? module.converter.toDatabase(merged) : merged;

          return tryMutate(() =>
            bulkApplySavedViewChanges(this.ydb, [
              {
                type: ViewChangeType.UPDATE_PROPERTIES,
                viewId,
                set: { [layoutType]: slot }
              }
            ])
          );
        })
    );
  }

  getProperties<K extends keyof PropertiesMap & string>(
    viewId: string,
    layoutType: K
  ): PropertiesMap[K] | undefined;
  getProperties(viewId: string): Record<string, unknown> | undefined;
  getProperties(
    viewId: string,
    layoutType?: string
  ): Record<string, unknown> | undefined {
    const view = this.ydb.viewsById[viewId];

    if (!view) {
      return undefined;
    }

    // Untyped: return the whole namespaced bag (all layouts' slots).
    if (!layoutType) {
      return view.properties;
    }

    const slot = view.properties?.[layoutType] as Record<string, unknown> | undefined;
    const module = this.viewLayoutRegistry?.getModule(layoutType);

    // Converter fills defaults on read, so an unconfigured layout returns its
    // canonical defaults rather than `undefined`.
    return module?.converter ? module.converter.fromDatabase(slot) : slot;
  }

  addSort(input: AddSortInput): Result<void, SdkError> {
    return parse(addSortSchema, input).andThen(({ viewId, fieldId, ascending }) =>
      assertViewExists(this.ydb, viewId)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() =>
          tryMutate(() =>
            bulkApplySavedViewChanges(this.ydb, [
              { type: ViewChangeType.APPEND_SORT, viewId, sort: { fieldId, ascending } }
            ])
          )
        )
    );
  }

  updateSort(input: UpdateSortInput): Result<void, SdkError> {
    return parse(updateSortSchema, input).andThen(({ viewId, fieldId, ascending }) =>
      assertViewExists(this.ydb, viewId)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() =>
          tryMutate(() =>
            bulkApplySavedViewChanges(this.ydb, [
              { type: ViewChangeType.UPDATE_SORT_VALUE, viewId, fieldId, ascending }
            ])
          )
        )
    );
  }

  removeSort(input: RemoveSortInput): Result<void, SdkError> {
    return parse(removeSortSchema, input).andThen(({ viewId, fieldId }) =>
      assertViewExists(this.ydb, viewId)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() =>
          tryMutate(() =>
            bulkApplySavedViewChanges(this.ydb, [
              { type: ViewChangeType.DELETE_SORT, viewId, fieldId }
            ])
          )
        )
    );
  }

  setFilter(input: SetFilterInput): Result<void, SdkError> {
    return parse(setFilterSchema, input).andThen(({ viewId, filter }) =>
      assertViewExists(this.ydb, viewId).andThen(() =>
        tryMutate(() =>
          bulkApplySavedViewChanges(this.ydb, [
            {
              type: ViewChangeType.SET_FILTER,
              viewId,
              newFilter: filter as DatabaseFilter,
              path: []
            }
          ])
        )
      )
    );
  }

  clearFilter(input: ClearFilterInput): Result<void, SdkError> {
    return parse(clearFilterSchema, input).andThen(({ viewId }) =>
      assertViewExists(this.ydb, viewId).andThen(() =>
        tryMutate(() =>
          bulkApplySavedViewChanges(this.ydb, [
            { type: ViewChangeType.CLEAR_FILTER, viewId }
          ])
        )
      )
    );
  }

  updateName(input: UpdateNameInput): Result<void, SdkError> {
    return parse(updateNameSchema, input).andThen(({ viewId, newName }) =>
      assertViewExists(this.ydb, viewId).andThen((view) =>
        tryMutate(() =>
          updateSavedViewName(this.ydb, viewId, [
            { delete: view.name.length },
            { insert: newName }
          ])
        )
      )
    );
  }

  applyNameDelta(input: ApplyViewNameDeltaInput): Result<void, SdkError> {
    return parse(applyViewNameDeltaSchema, input).andThen(({ viewId, delta }) =>
      assertViewExists(this.ydb, viewId).andThen(() =>
        tryMutate(() => updateSavedViewName(this.ydb, viewId, delta))
      )
    );
  }

  hideFields(input: HideFieldsInput): Result<void, SdkError> {
    return parse(hideFieldsSchema, input).andThen(({ viewId, fieldIds }) => {
      const viewCheck = assertViewExists(this.ydb, viewId);

      if (viewCheck.isErr()) {
        return err(viewCheck.error);
      }

      for (const id of fieldIds) {
        const fieldCheck = assertFieldExists(this.ydb, id);

        if (fieldCheck.isErr()) {
          return err(fieldCheck.error);
        }
      }

      return tryMutate(() =>
        bulkApplySavedViewChanges(
          this.ydb,
          fieldIds.map((fieldId) => ({
            type: ViewChangeType.UPDATE_FIELDS as const,
            viewId,
            fieldKey: fieldId,
            fieldValue: false
          }))
        )
      );
    });
  }

  showFields(input: ShowFieldsInput): Result<void, SdkError> {
    return parse(showFieldsSchema, input).andThen(({ viewId, fieldIds }) => {
      const viewCheck = assertViewExists(this.ydb, viewId);

      if (viewCheck.isErr()) {
        return err(viewCheck.error);
      }

      for (const id of fieldIds) {
        const fieldCheck = assertFieldExists(this.ydb, id);

        if (fieldCheck.isErr()) {
          return err(fieldCheck.error);
        }
      }

      return tryMutate(() =>
        bulkApplySavedViewChanges(
          this.ydb,
          fieldIds.map((fieldId) => ({
            type: ViewChangeType.UPDATE_FIELDS as const,
            viewId,
            fieldKey: fieldId,
            fieldValue: true
          }))
        )
      );
    });
  }

  reorder(input: ReorderViewInput): Result<void, SdkError> {
    return parse(reorderViewSchema, input).andThen(({ viewId, newIndex }) =>
      assertViewExists(this.ydb, viewId)
        .andThen(() => assertIndexInRange(newIndex, this.ydb.views.length - 1))
        .andThen(() => {
          const currentIndex = this.ydb.views.indexOf(viewId);
          // addBefore removes the item first, shifting subsequent indices down by one.
          // Compensate for forward moves so the item lands at the caller's intended position.
          const adjustedIndex = newIndex > currentIndex ? newIndex + 1 : newIndex;

          return tryMutate(() => reorderSavedView(this.ydb, viewId, adjustedIndex));
        })
    );
  }

  isViewsEmpty(): boolean {
    return this.ydb.views.length === 0;
  }

  getViewCount(): number {
    return this.ydb.views.length;
  }

  hasSortForField(viewId: string, fieldId: string): boolean {
    const view = this.ydb.viewsById[viewId];

    if (!view) {
      return false;
    }

    return view.sorts.toArray().some((sort) => sort.fieldId === fieldId);
  }

  getViewLayoutType(viewId: string): DatabaseLayoutType | string | undefined {
    const view = this.ydb.viewsById[viewId];

    if (!view) {
      return undefined;
    }

    return view.layoutType;
  }

  getIdByName(name: string): string | undefined {
    const views = this.ydb.views;

    return views.find((id: string) => {
      const view = this.ydb.viewsById[id];
      return view?.name === name;
    });
  }

  list(): readonly DatabaseView[] {
    return this.ydb.views.flatMap((viewId) => {
      const view = this.ydb.viewsById[viewId];
      return view ? [collabViewToDatabaseView(viewId, view.toJSON())] : [];
    });
  }
}
